import { BaseApplicant as Applicant, isNonEmployedJobType } from '@applyonline/shared'
import { formatCurrency, formatPhone } from '@if/core/utils'
import { first } from 'lodash-es'
import { useTranslation } from 'react-i18next'
interface Props {
  applicant: Applicant
  gotoStep: (target: string) => void
}
const ReviewApplicantInfo = ({ applicant, gotoStep }: Props) => {
  const { t } = useTranslation()
  return (
    <>
      <div className="review-item">
        <h1 className="h3">{t('common.aboutYourself')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('personalInformation.preferredLanguage')} : </strong>
              <span>{t(`ELanguage.${applicant.languageId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourFirstName')} : </strong>
              <span>{applicant.firstName}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourLastName')} : </strong>
              <span>{applicant.lastName}</span>
            </p>
            <p>
              <strong>{t('confirmation.mobilePhone')} : </strong>
              <span>{formatPhone(applicant.cellPhone)}</span>
            </p>
            {applicant.homePhone && (
              <p>
                <strong>{t('confirmation.homePhone')} : </strong>
                <span>{formatPhone(applicant.homePhone)}</span>
              </p>
            )}
            <p>
              <strong>{t('confirmation.email')} : </strong>
              <span>{applicant.email}</span>
            </p>
          </div>
          <button type="button" className="btn-action btn-simple" onClick={() => gotoStep('personal-information')}>
            {t('review.modify')}
          </button>
        </div>
      </div>

      <div className="review-item">
        <h1 className="h3">{t('common.personalDetails')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('personalInformation.yourGender')} : </strong>
              <span>{t(`EGender.${applicant.genderId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('confirmation.birthDate')} : </strong>
              <span>{applicant.birthDate}</span>
            </p>
            {applicant.sin && (
              <p>
                <strong>{t('personalInformation.sin')} : </strong>
                <span>{applicant.sin}</span>
              </p>
            )}
            <p>
              <strong>{t('confirmation.address')} : </strong>
              <span>
                {applicant.currentAddress.apartment && `${applicant.currentAddress.apartment}-`}
                {applicant.currentAddress.civicNumber} {applicant.currentAddress.street},{' '}
                {applicant.currentAddress.city}, {applicant.currentAddress.stateIso}{' '}
                {applicant.currentAddress.postalCode}
              </span>
            </p>
            <p>
              <strong>{t('common.since')} : </strong>
              {Math.floor(applicant.currentAddress.months / 12) > 0 && (
                <span>
                  {t('common.yearsLabel', {
                    count: Math.floor(applicant.currentAddress.months / 12),
                  })}
                  &nbsp;
                </span>
              )}
              {applicant.currentAddress.months % 12 > 0 && (
                <span>
                  {t('common.monthsLabel', {
                    count: applicant.currentAddress.months % 12,
                  })}
                </span>
              )}
            </p>
            <p>
              <strong>{t('housing.homeFeeType')} : </strong>
              <span>{t(`EHomeStatus.${applicant.spending.homeFeeTypeId}`, { ns: 'enum' })}</span>
            </p>
          </div>
          <button type="button" className="btn-action btn-simple" onClick={() => gotoStep('contact')}>
            {t('review.modify')}
          </button>
        </div>
      </div>

      <div className="review-item">
        <h1 className="h3">{t('income.howMuch')}</h1>
        <div className="box">
          <div className="box-content">
            {applicant.currentJobs?.[0] && !isNonEmployedJobType(applicant.currentJobs[0].jobType) && (
              <>
                <p>
                  <strong>{t('income.category')} : </strong>
                  <span> {t(`EJobType.${applicant.currentJobs[0].jobType}`, { ns: 'enum' })}</span>
                </p>
                <p>
                  <strong>{t('income.jobTitle')} : </strong>
                  <span> {applicant.currentJobs[0].jobTitle}</span>
                </p>
                <p>
                  <strong>{t('income.beforeDeductions')} : </strong>
                  <span> {formatCurrency(applicant.currentJobs[0].annualSalary)}</span>
                </p>
                <p>
                  <strong>{t('income.employerName')} : </strong>
                  <span> {applicant.currentJobs[0].employerName}</span>
                </p>
                <p>
                  <strong>{t('income.phoneNumber')} : </strong>
                  <span> {formatPhone(applicant.currentJobs[0].employerPhone)}</span>
                </p>
                {applicant.currentJobs[0].employerPhoneExt && (
                  <p>
                    <strong>{t('income.extension')} : </strong>
                    <span> {applicant.currentJobs[0].employerPhoneExt}</span>
                  </p>
                )}
                {applicant.currentJobs[0].months && (
                  <p>
                    <strong>{t('income.employmentLenght')} : </strong>
                    {Math.floor(applicant.currentJobs[0].months / 12) > 0 && (
                      <span>
                        {t('common.yearsLabel', {
                          count: Math.floor(applicant.currentJobs[0].months / 12),
                        })}
                        &nbsp;
                      </span>
                    )}
                    {applicant.currentJobs[0].months % 12 > 0 && (
                      <span>
                        {t('common.monthsLabel', {
                          count: applicant.currentJobs[0].months % 12,
                        })}
                      </span>
                    )}
                  </p>
                )}
              </>
            )}
            <p>
              <strong>{t('income.otherIncome')} : </strong>
              <span> {formatCurrency(first(applicant.otherIncomes)?.annualAmount)}</span>
            </p>
          </div>
          <button type="button" className="btn-action" onClick={() => gotoStep('employment')}>
            {t('review.modify')}
          </button>
        </div>
      </div>
    </>
  )
}

export default ReviewApplicantInfo
